import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import { isTelemedLocation, isReliantVirtualVisit } from '../../core/util/location';
import EmergencyModal from '../EmergencyModal';
import TelemedModal from '../TelemedModal';
import { analyticsTrackEvent } from '../../core/analytics';
import { SYMPTOM_TOUCH_REASON } from '../../core/analytics/events';
import { apiGetRuntime, apiGetJson, apiPostRuntime } from '../../core/dapi';
import {
  getSymptomsListUrl,
  getSymptomsIsEmergencyUrl,
  getSymptomIsNotSuitableForTelemedUrl,
} from '../../core/dapi/symptom';
import { isEmptyString } from '../../core/util/string';
import { isEmptyArray } from '../../core/util/array';
import { emptyFunction } from '../../core/util/function';
import { fromArrayToReactSelectObjArray } from '../../core/util/symptoms';
import {
  DEFAULT_URGENT_CARE_REASON_FOR_VISIT,
  AUTOSUGGEST_MINIMUM_INPUT_CHARS,
} from '../../config/index';
import logger from '../../core/logger/index';
import { ajaxSuccess } from '../../actions/runtime';
import { showModal, hideModal } from '../../actions/runtime';
import { isEmptyObject } from '../../core/util/object';
import { getUniversalSearchUrl } from '../../core/dapi/universalSearch';
import { colors } from '../../constants/colors';
import { fontSize } from '../../constants/text';
import { EMPTY_ARRAY } from '../../core/util/array';

const Error = styled.div.withConfig({ componentId: 'sc-kls1m6-0' })`
  color: ${colors.ladyInRed};
  font-size: ${fontSize.default};
`;

const SUGGESTIONS = 'symptom-suggestions';

const mapStateToProps = (state: any) => {
  let suggestions = [];
  if (
    state.runtime.ajax[SUGGESTIONS] &&
    state.runtime.ajax[SUGGESTIONS].result &&
    state.runtime.ajax[SUGGESTIONS].result.results
  ) {
    suggestions = fromArrayToReactSelectObjArray(state.runtime.ajax[SUGGESTIONS].result.results);
  }

  return { suggestions, modalState: state.runtime.modal || {} };
};

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
  fetchSuggestions: (input: any) => {
    if (!isEmptyString(input) && input.length >= AUTOSUGGEST_MINIMUM_INPUT_CHARS) {
      const { shouldUseUniversalSearch } = ownProps;
      // SOLV-4803 : to maintain backward compatibility, only use universal search autosuggest
      // when specified explicitly
      if (shouldUseUniversalSearch) {
        const url = getUniversalSearchUrl();
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 6 arguments, but got 4.
        apiPostRuntime(dispatch, SUGGESTIONS, url, { query: input });
      } else {
        const url = getSymptomsListUrl(input, 100);
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 3.
        apiGetRuntime(dispatch, SUGGESTIONS, url);
      }
    }
  },

  clearSuggestions: () => {
    dispatch(ajaxSuccess(SUGGESTIONS)([]));
  },

  toggleEmergencyModal: (value: any) => {
    if (value) {
      dispatch(showModal('emergency'));
    } else {
      dispatch(hideModal('emergency'));
    }
  },

  toggleTelemedModal: (value: any) => {
    if (value) {
      dispatch(showModal('telemed'));
    } else {
      dispatch(hideModal('telemed'));
    }
  },
});

const getSuggestionValue = (symptom: any) => symptom.value;

const getSuggestions = (allSuggestions: any, value: any) => {
  if (!value || isEmptyArray(allSuggestions)) {
    return EMPTY_ARRAY;
  }

  const inputValue = value.trim().toLowerCase();
  const inputLength = inputValue.length;
  const words = inputValue.split(/\s+/);
  const toReturn =
    inputLength === 0
      ? []
      : allSuggestions.filter((symptom: any) => {
          const suggestedSymptom = symptom.value.toLowerCase();
          for (const word of words) {
            if (!suggestedSymptom.includes(word)) {
              return false;
            }
          }

          return true;
        });
  return toReturn.length === 0 ? EMPTY_ARRAY : toReturn.splice(0, 10);
};

type OwnSymptomInputProps = {
  field: any;
  s: any;
  id: string;
  label?: string;
  placeholder?: string;
  onFocus?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  toggleEmergencyModal?: (...args: any[]) => any;
  toggleTelemedModal?: (...args: any[]) => any;
  errorInline?: boolean;
  suggestions?: any[];
  fetchSuggestions?: (...args: any[]) => any;
  clearSuggestions?: (...args: any[]) => any;
  autoFocus?: boolean;
  sourceOfInput?: string;
  inputProps?: any;
  submitted?: boolean;
  onSuggestionSelected?: (...args: any[]) => any;
  location?: any;
  shouldUseUniversalSearch?: boolean;
  modalState?: any;
};

type SymptomInputState = any;

type SymptomInputProps = OwnSymptomInputProps & typeof SymptomInput.defaultProps;

class SymptomInput extends Component<SymptomInputProps, SymptomInputState> {
  static defaultProps = {
    placeholder: DEFAULT_URGENT_CARE_REASON_FOR_VISIT,
    errorInline: false,
    toggleEmergencyModal: emptyFunction,
    toggleTelemedModal: emptyFunction,
    onFocus: emptyFunction,
    onBlur: emptyFunction,
    onSuggestionSelected: emptyFunction,
  };

  constructor(props: SymptomInputProps) {
    super(props);
    this.state = {
      searchValue: props.field.value,
      currentSymptomObject: null,
      touched: false,
    };
  }

  onSuggestionSelected = (event: any) => {
    event.preventDefault();
    this.props.onSuggestionSelected(event);
  };

  onSuggestionsClearRequested = () => {
    // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    this.props.clearSuggestions();
  };

  onSuggestionsFetchRequested = ({ value }: any) => {
    if (this.isAlreadySuggested(value.trim())) {
      return true;
    }

    // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    this.props.fetchSuggestions(value);
    return false;
  };

  handleOnChange = (event: any, action: any) => {
    switch (action.method) {
      case 'down':
      case 'up': {
        const newSymptoms = action.newValue;
        return this.props.field.onChange(newSymptoms);
      }

      case 'click':
      case 'type':
      case 'enter': {
        const newSymptoms = action.newValue;

        if (action.method === 'enter') {
          // we want to track to segment only if you enter not if you click or type
          const trackingProperties = {
            numSymptoms: 1,
            sourceOfInput: this.props.sourceOfInput,
          };
          analyticsTrackEvent(SYMPTOM_TOUCH_REASON, trackingProperties);
        }
        const symptomObjects = getSuggestions(this.props.suggestions, newSymptoms);
        this.setState({ currentSymptomObject: symptomObjects[0] });

        this.setState({ searchValue: newSymptoms });
        return this.props.field.onChange(newSymptoms);
      }

      case 'escape':
      default:
        return null;
    }
  };

  handleFocus = () => {
    this.props.onFocus(this.props.id);
  };

  handleBlur = () => {
    this.setState({ touched: true });
    this.conditionallyShowEmergencyToolTip(this.state.searchValue);

    if (
      !isEmptyObject(this.props.location) &&
      isTelemedLocation(this.props.location) &&
      !isReliantVirtualVisit(this.props.location)
    ) {
      this.conditionallyShowTelemedTooltip(this.state.searchValue);
    }

    this.props.onBlur(this.props.id);
  };

  closeEmergencyModal = () => this.props.toggleEmergencyModal(false);

  closeTelemedModal = () => this.props.toggleTelemedModal(false);

  displayErrors = (f: any, showErrors: any) =>
    (this.props.submitted || this.state.touched) && f.error && showErrors;

  isAlreadySuggested = (value: any) => getSuggestions(this.props.suggestions, value).length > 0;

  conditionallyShowEmergencyToolTip = (newSymptom: any) => {
    if (isEmptyString(newSymptom)) {
      return;
    }

    if (!this.state.currentSymptomObject) {
      apiGetJson(getSymptomsIsEmergencyUrl(newSymptom))
        .then((response: any) => {
          if (response.data.is_emergency) {
            this.props.toggleEmergencyModal(true);
          }
        })
        .catch((err: any) => logger.debug(err));
    } else if (this.state.currentSymptomObject.isEmergency) {
      this.props.toggleEmergencyModal(true);
    }
  };

  conditionallyShowTelemedTooltip = (newSymptom: any) => {
    if (isEmptyString(newSymptom)) {
      return;
    }

    apiGetJson(getSymptomIsNotSuitableForTelemedUrl(newSymptom))
      .then((response: any) => {
        if (response.data.is_not_good_for_telemed) {
          this.props.toggleTelemedModal(true);
        }
      })
      .catch((err: any) => logger.debug(err));
  };

  renderSuggestion = (symptom: any, { isHighlighted }: any) => {
    const className = isHighlighted ? this.props.s.suggestionHighlighted : '';

    return <div className={className}>{symptom.value}</div>;
  };

  render = () => {
    const { field, s, inputProps, location, modalState } = this.props;
    const { touched } = this.state;
    const fieldLabel = `${this.props.id}Label`;
    const value = !isEmptyObject(inputProps) ? inputProps.value : field.value;

    return (
      <div className={s.formFieldGroup}>
        <div
          className={
            this.displayErrors(field, this.props.errorInline) ? s.formFieldInlineError : s.formField
          }
        >
          {!isEmptyString(this.props.label) && (
            <label className={s[fieldLabel]} htmlFor={this.props.id} id={fieldLabel}>
              {this.props.label}
            </label>
          )}

          <Autosuggest
            // @ts-expect-error ts-migrate(2769) FIXME: Property 'focusFirstSuggestion' does not exist on ... Remove this comment to see the full error message
            focusFirstSuggestion
            getSuggestionValue={getSuggestionValue}
            id="symptoms"
            inputProps={{
              ...inputProps,
              id: this.props.id,
              onChange: this.handleOnChange,
              value: !isEmptyString(value) ? value : '',
              placeholder: this.props.placeholder,
              onFocus: this.handleFocus,
              onBlur: this.handleBlur,
              autoFocus: this.props.autoFocus,
            }}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.onSuggestionSelected}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            renderSuggestion={this.renderSuggestion}
            suggestions={getSuggestions(this.props.suggestions, this.state.searchValue)}
            theme={this.props.s}
          />

          {this.displayErrors(field, !this.props.errorInline) && <Error>{field.error}</Error>}
        </div>

        {modalState.emergency && <EmergencyModal />}
        {!isEmptyObject(location) && isTelemedLocation(location) && modalState.telemed && (
          <TelemedModal closeTelemedModal={this.closeTelemedModal} location={this.props.location} />
        )}
      </div>
    );
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SymptomInput);
